/**
 * @description Renders the personnel sync administration - history tab for viewing the last X records
 *  of personnel sync runs
 */

import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import NoResults from '@seccon/shared/ui/components/NoResults';
import SortableCell from '@seccon/shared/ui/components/SortableCell';
import LinkedCell from '@seccon/shared/ui/components/LinkedCell';
import useClientSidePagination from '@seccon/shared/ui/hooks/useClientSidePagination';
import { DESC, getNumericOrder, compareIsoDates } from '@seccon/shared/utils/sort-compare';
import { formatLongIsoDate } from '@seccon/shared/utils/format';

import useSyncHistory from '../hooks/useSyncHistory';

// API limit for the number of records displayed
const HISTORICAL_RECORDS_LIMIT = 50;

const styles = {
  alert: {
    width: 'fit-content'
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    width: '100%'
  },
  loadingContainer: {
    alignItems: 'center'
  },
  noResults: {
    marginTop: 3
  },
  table: {
    width: '100%'
  },
  tableCellCenter: {
    cell: {
      textAlign: 'center'
    }
  },
  tableContainer: {
    minWidth: '925px',
    width: '90%'
  },
  tableHeader: {
    label: {
      display: 'flex',
      justifyContent: 'center'
    }
  }
};

/**
 * Sort function that orders the rows based on the length of the unmapped headers array
 * @param {string[]} valueA - row A unmapped header array
 * @param {string[]} valueB - row B unmapped header array
 * @param {string} order - ascending or descending order of the column
 * @returns {number} sort order
 */
function compareUnmappedHeaders (valueA, valueB, order) {
  // Get the isGreaterThan/isLessThan values based on the sort order
  const [isGreaterThan, isLessThan] = getNumericOrder(order);

  // Compare the array lengths and return the results
  const countA = valueA.length;
  const countB = valueB.length;

  if (countA === countB) {
    return 0;
  }

  return countA > countB ? isGreaterThan : isLessThan;
}

// Sort compare map for special columns
const compareMap = {
  runDate: compareIsoDates,
  unmappedHeaders: compareUnmappedHeaders
};

const History = () => {
  // Retrieve the sync history data
  const { data: history, error } = useSyncHistory();

  // Paginate the sync history data and retrieve callbacks for handling various table events
  const {
    handleChangeRowsPerPage,
    handlePageChange,
    handleSort,
    page,
    pageData,
    order,
    orderBy,
    rowsPerPage,
    total
  } = useClientSidePagination({
    compareMap,
    data: history,
    defaultOrder: DESC,
    defaultOrderBy: 'runDate'
  });

  // If the history has not loaded yet (and there is no error), display a loading overlay
  if (history === null && !error) {
    return (
      <Stack spacing={3} sx={styles.loadingContainer}>
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
      </Stack>
    );
  }

  // If an error occured, display an error banner
  if (error) {
    return (
      <NoResults sx={styles.noResults}>
        Personnel sync history could not be loaded.
      </NoResults>
    );
  }

  // If sync history is empty, display an empty banner
  if (history.length === 0) {
    return (
      <NoResults sx={styles.noResults}>
        No personnel sync history to display.
      </NoResults>
    );
  }

  return (
    <Stack sx={styles.container} spacing={3}>
      {/* Display a warning alert if the API limit has been reached (because not all sync results can be displayed) */}
      {total >= HISTORICAL_RECORDS_LIMIT && (
        <Alert severity='warning'>Personnel sync history table only displays the last {HISTORICAL_RECORDS_LIMIT} runs.</Alert>
      )}

      <Paper sx={styles.tableContainer}>
        <TableContainer>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow>
                <SortableCell
                  sx={styles.tableHeader}
                  id='runType'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Run Type
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='runBy'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Ran By
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='runDate'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Run Date and Time
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='syncOnSsn'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Sync on Ssn
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='mappingName'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Mapping Definition
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='successfulSyncs'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Successful Syncs
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='partialSyncs'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Partial Syncs
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='unmappedHeaders'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Unmapped Values
                </SortableCell>
                <SortableCell
                  sx={styles.tableHeader}
                  id='failedSyncs'
                  onSort={handleSort}
                  order={order}
                  orderBy={orderBy}
                >
                  Failed Syncs
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.map((row) => {
                // Prepare the row drilldown url
                const drilldownUrl = `details/${ row.id }`;

                return (
                  <TableRow hover key={`row${ row.id }`}>
                    <LinkedCell href={drilldownUrl}>
                      {{
                        AUTOMATED: 'Automated',
                        ON_DEMAND: 'On Demand'
                      }[row.runType] ?? row.runType}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl}>
                      {row.runBy}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl}>
                      {formatLongIsoDate(row.runDate)}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl}>
                      {row.syncOnSsn ? 'Yes' : 'No'}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl}>
                      {row.mappingName}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl} sx={styles.tableCellCenter}>
                      {row.successfulSyncs}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl} sx={styles.tableCellCenter}>
                      {row.partialSyncs}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl} sx={styles.tableCellCenter}>
                      {row.unmappedHeaders.length}
                    </LinkedCell>
                    <LinkedCell href={drilldownUrl} sx={styles.tableCellCenter}>
                      {row.failedSyncs}
                    </LinkedCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component='div'
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, value) => handlePageChange(value)}
          onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
        />
      </Paper>
    </Stack>
  );
};

export default History;
